"use client";

import { useMemo } from "react";
import { withErrorBoundary } from "@sentry/nextjs";
import dynamic from "next/dist/shared/lib/dynamic";
import { WHIMSTAY_PROD_URL_S3_US_EAST_AMAZONAWS } from "@/constant";
import TaskStatusIcon from "components/icons-svg/TaskStatus";
import { useDisclosure } from "utils/hooks/useDisclosure";
import Amenity from "./Amenity";
import { getAllAmenities } from "./getAllAmenities";
import { Amenities } from "./type";
const AmenitiesModal = dynamic(() => import( /* webpackChunkName: "AmenitiesModal" */"./AmenitiesModal"), {
  ssr: false
});
const PropertyAmenities = (props: {
  amenities: Amenities[];
  propertyId: string;
}) => {
  const {
    amenities,
    propertyId
  } = props;
  const {
    isOpen: isMoreAmenitiesModalOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const allAmenities = useMemo(() => getAllAmenities(amenities), [amenities]);

  // If total ≥ 8: show 8
  // If total < 8: show up to 6
  // Always display popular first, then non-popular
  const popular9Amenities = useMemo(() => {
    const all = getAllAmenities(amenities);
    type AmenityItem = (typeof all)[number];
    const sortAmenities = (a: AmenityItem, b: AmenityItem) => {
      const aPriority = a.pdp_priority;
      const bPriority = b.pdp_priority;
      if (aPriority != null && bPriority != null) {
        return aPriority - bPriority;
      }
      if (aPriority != null && bPriority == null) return -1;
      if (aPriority == null && bPriority != null) return 1;
      if (a.is_popular && !b.is_popular) return -1;
      if (!a.is_popular && b.is_popular) return 1;
      return a.id - b.id;
    };
    const sorted = [...all].sort(sortAmenities);
    return sorted.length >= 8 ? sorted.slice(0, 8) : sorted.slice(0, 6);
  }, [amenities]);
  return <>
      <div className='my-8 h-[1px]  bg-grayBorder'></div>
      <div className='mb-6 flex items-center gap-2'>
        <TaskStatusIcon className='h-[16px] w-[16px] md:h-[16.25px] md:w-[20px]' data-sentry-element="TaskStatusIcon" data-sentry-source-file="PropertyAmenities.tsx" />
        <h3 className='text-base font-semibold leading-[19.2px] tracking-[-0.24px] text-secondary500 md:text-xl md:leading-[30px] md:tracking-[-0.3px]'>
          What’s included
        </h3>
      </div>
      <div id='what-this-place-offers' className='scroll-mt-28 lg4:flex'>
        <div className='bg-white lg4:grow'>
          <div className='grid grid-cols-2 gap-x-8 gap-y-3'>
            {popular9Amenities.map(amenity => <div key={amenity.id}>
                <Amenity icon={<div className='flex h-8 w-8 min-w-[32px] items-center justify-center rounded-full bg-grayBorder font-semibold'>
                      {amenity.key_name.charAt(0)}
                    </div>} src={amenity.key_icon?.replace(WHIMSTAY_PROD_URL_S3_US_EAST_AMAZONAWS, process.env.NEXT_PUBLIC_BUCKET_HOST || "")} name={amenity.key_name} />
              </div>)}
          </div>
          {amenities && allAmenities?.length > 8 && <button type='button' onClick={onOpen} className='btn mt-6 h-6 bg-white p-0 text-sm font-semibold text-accent-500 underline md:h-7 md:text-base' data-track-v2-event='PDP_AMENITIES_CLICK' data-track-v2-props={JSON.stringify({
          propertyId: propertyId || ""
        })}>
              Show all {allAmenities.length} amenities
            </button>}
        </div>
      </div>
      {/* lazy load this , bcoz priority of open show modal is low */}
      {isMoreAmenitiesModalOpen && <AmenitiesModal isOpen={isMoreAmenitiesModalOpen} onClose={onClose} amenities={amenities} />}
    </>;
};
export default withErrorBoundary(PropertyAmenities, {
  beforeCapture: scope => {
    scope.setTag("ws_location", "PropertyAmenities");
  }
});